/**
 * 顶栏菜单：分组结构（一级 hover dropdown）+ 二级链接列表。
 *
 * 设计原则：
 * - 菜单用用户任务命名，不要求先理解量化或工程术语
 * - 常用入口优先，专业能力保留但使用通俗标签
 * - 每个 group 至少有一个 item 启用就显示（item 按 module 过滤）
 * - 单 item 的 group（首页 / 设置 / 沙盒）渲染为直接链接
 */

export interface NavItem {
  to: string;            // 唯一 id（用 path）
  label: string;
  module: string;        // 所属模块 id；用于和 modules.ts 的开关联动
}

export interface NavGroup {
  id: string;            // group 标识
  label: string;         // 显示文字（含 emoji）
  items: NavItem[];      // 二级链接；length=1 时一级直接渲染该 item
}

const LEGACY_CATALOG_GROUPS: NavGroup[] = [
  {
    id: "home",
    label: "🏠 首页",
    items: [{ to: "/", label: "🏠 今日工作台", module: "core" }],
  },
  {
    id: "research",
    label: "🔭 机会研究",
    items: [
      { to: "/scanner", label: "🎯 条件选股", module: "core" },
      { to: "/lab/adaptive", label: "🔬 研究台", module: "core" },
      { to: "/more-tools", label: "🧰 更多工具", module: "core" },
      { to: "/library", label: "📚 策略与因子库", module: "core" },
      { to: "/rps", label: "📈 RPS 强势榜", module: "briefing" },
      { to: "/theme-radar", label: "🧭 热门主题", module: "briefing" },
      { to: "/market-brief", label: "🩺 市场体检", module: "briefing" },
      { to: "/rotation", label: "🔄 行业强弱", module: "briefing" },
      { to: "/topgun", label: "🌡️ 过热提醒", module: "briefing" },
      { to: "/etf", label: "🌊 ETF 资金流向", module: "briefing" },
      { to: "/patterns", label: "📐 相似走势", module: "patterns" },
      { to: "/analog", label: "🔮 历史走势", module: "patterns" },
      { to: "/stocks", label: "📋 全部股票", module: "core" },
      { to: "/block-trades", label: "🤝 大宗交易", module: "core" },
      { to: "/indicators", label: "🧬 因子实验室", module: "briefing" },
      { to: "/rps/backtest", label: "🧪 条件验证", module: "briefing" },
      { to: "/grid/research", label: "🧪 网格研究", module: "lab" },
      { to: "/lab/research-agents", label: "🔬 因子研究与研究任务", module: "lab" },
      { to: "/briefing", label: "📰 每日早报", module: "briefing" },
    ],
  },
  {
    id: "portfolio",
    label: "👁️ 组合管理",
    items: [
      { to: "/watchlist", label: "⭐ 自选与跟踪", module: "core" },
      { to: "/positions", label: "💰 持仓与收益", module: "positions" },
      { to: "/alerts", label: "🚨 价格与风险提醒", module: "alerts" },
      { to: "/intraday-tracking", label: "⏱️ 盘中观察", module: "alerts" },
      { to: "/swing", label: "📋 波段机会", module: "positions" },
      { to: "/risk", label: "⚠️ 持仓风险", module: "positions" },
      { to: "/ml/forward-tracking", label: "📍 AI 预测跟踪", module: "ml" },
      { to: "/screen-runs", label: "🔀 选股变化", module: "briefing" },
      { to: "/news", label: "📡 新闻", module: "news" },
      { to: "/watch-news", label: "🕸️ 关注的新闻来源", module: "news" },
      { to: "/notifications", label: "📬 通知历史", module: "alerts" },
      { to: "/journal", label: "📔 决策日记", module: "positions" },
    ],
  },
  {
    id: "execution",
    label: "🤖 策略执行",
    items: [
      { to: "/quant-agents", label: "🤖 自动策略管理（模拟交易）", module: "lab" },
      { to: "/grid", label: "🎯 日内降成本", module: "positions" },
      { to: "/broker-trading", label: "🖥️ 交易中心", module: "trading" },
      { to: "/lab/sandbox", label: "🧪 策略实验沙盒", module: "lab" },
      { to: "/lab", label: "🗂️ 策略股票池", module: "lab" },
      { to: "/lab/coint", label: "🔗 配对交易研究", module: "lab" },
      { to: "/ml/experiments", label: "🧪 AI 预测实验", module: "ml" },
      { to: "/ml/compare", label: "⚖️ 模型效果对比", module: "ml" },
      { to: "/ml/publishing", label: "📤 共享配置发布", module: "core" },
      { to: "/ml/jumps", label: "⚡ 异常归因", module: "ml" },
      { to: "/auto-tune", label: "🎛️ 自动调参", module: "ml" },
      { to: "/t-recap", label: "🔍 日内交易复盘", module: "positions" },
      { to: "/ask", label: "💬 研究问答", module: "briefing" },
      { to: "/copilot", label: "🤖 AI 助手工作台", module: "briefing" },
    ],
  },
  {
    id: "settings",
    label: "⚙️ 设置",
    items: [
      { to: "/settings", label: "🛠️ 通用设置", module: "settings" },
      { to: "/settings/accounts", label: "👤 账户管理", module: "settings" },
      { to: "/settings/modules", label: "🧩 功能模块", module: "settings" },
      { to: "/settings/usage", label: "📊 使用统计", module: "settings" },
      { to: "/settings/errors", label: "🐛 系统错误", module: "settings" },
      { to: "/settings/mcp", label: "🔌 开发接口", module: "settings" },
      { to: "/skills", label: "🧩 技能中心", module: "settings" },
      { to: "/skills/history", label: "📜 技能运行历史", module: "settings" },
      { to: "/wechat-log", label: "💬 微信运行日志", module: "settings" },
    ],
  },
];

const CATALOG_ITEMS = new Map(LEGACY_CATALOG_GROUPS.flatMap(group => group.items).map(item => [item.to, item]));
const catalogItems = (paths: string[]) => paths.map(path => {
  const item = CATALOG_ITEMS.get(path);
  if (!item) throw new Error(`Unknown catalog path: ${path}`);
  return item;
});

export const NAV_CATALOG_GROUPS: NavGroup[] = [
  { id: "today", label: "🏠 今日", items: catalogItems(["/", "/briefing", "/market-brief", "/news", "/watch-news", "/notifications"]) },
  { id: "market", label: "📈 行情与机会", items: catalogItems(["/stocks", "/block-trades", "/scanner", "/rps", "/theme-radar", "/rotation", "/topgun", "/etf", "/patterns", "/analog", "/more-tools"]) },
  { id: "research-workspace", label: "🔬 研究", items: catalogItems(["/lab/adaptive", "/lab", "/lab/coint", "/rps/backtest", "/ml/forward-tracking", "/ml/jumps", "/ask"]) },
  { id: "library", label: "📚 策略与因子库", items: catalogItems(["/library", "/ml/publishing"]) },
  { id: "watchlist", label: "⭐ 自选", items: catalogItems(["/watchlist"]) },
  { id: "trading-workspace", label: "💼 交易", items: catalogItems(["/positions", "/quant-agents", "/broker-trading", "/grid", "/risk", "/t-recap", "/journal", "/swing", "/alerts", "/intraday-tracking"]) },
  { id: "copilot", label: "🤖 AI 助手", items: catalogItems(["/copilot"]) },
  { id: "settings", label: "⚙️ 设置", items: LEGACY_CATALOG_GROUPS.find(group => group.id === "settings")!.items },
];

/** 兼容老调用：把所有 item 摊平。⌘K 命令面板等用。 */
const NAV_BY_PATH = new Map(
  NAV_CATALOG_GROUPS.flatMap((group) => group.items).map((item) => [item.to, item]),
);

/** Keep custom shortcut wording while sharing the feature's navigation icon. */
export function withFeatureIcon(path: string, label: string): string {
  const iconPattern = /^(\p{Extended_Pictographic}\uFE0F?)/u;
  if (iconPattern.test(label)) return label;
  const item = NAV_BY_PATH.get(path.split(/[?#]/)[0] ?? path);
  const icon = item?.label.match(iconPattern)?.[1] ?? "🧰";
  return `${icon} ${label}`;
}

function navItems(paths: string[]): NavItem[] {
  return paths.map((path) => {
    const item = NAV_BY_PATH.get(path);
    if (!item) throw new Error(`Unknown navigation path: ${path}`);
    return item;
  });
}

export const NAV_GROUPS: NavGroup[] = [
  {
    id: "today",
    label: "🏠 今日",
    items: navItems(["/", "/briefing", "/market-brief", "/news", "/watch-news", "/notifications"]),
  },
  {
    id: "market",
    label: "📈 行情",
    items: navItems(["/stocks", "/block-trades"]),
  },
  {
    id: "opportunities",
    label: "🔎 机会",
    items: [
      ...navItems([
      "/scanner", "/rps", "/theme-radar", "/rotation", "/topgun", "/etf",
      ]),
      { to: "/more-tools", label: "🧰 更多工具", module: "core" },
    ],
  },
  {
    id: "research-workspace",
    label: "🔬 研究",
    items: navItems(["/lab/adaptive"]),
  },
  {
    id: "library",
    label: "📚 策略与因子库",
    items: navItems(["/library", "/ml/publishing"]),
  },
  {
    id: "watchlist",
    label: "⭐ 自选",
    items: navItems(["/watchlist"]),
  },
  {
    id: "trading-workspace",
    label: "💼 交易",
    items: navItems([
      "/positions", "/quant-agents", "/broker-trading", "/grid", "/risk", "/t-recap", "/journal",
      "/swing", "/alerts", "/intraday-tracking",
    ]),
  },
  {
    id: "copilot",
    label: "🤖 AI 助手",
    items: navItems(["/copilot"]),
  },
  {
    id: "settings",
    label: "⚙️ 设置",
    items: navItems([
      "/settings", "/settings/accounts", "/settings/modules", "/settings/usage",
      "/settings/errors", "/settings/mcp",
    ]),
  },
];

export const NAV_ITEMS: NavItem[] = NAV_GROUPS.flatMap((g) => g.items);

/** 按 nav_order（path 列表）重排，未在 order 里的按 NAV_ITEMS 顺序追加。 */
export function applyNavOrder(items: NavItem[], order: string[] | undefined): NavItem[] {
  if (!order || order.length === 0) return items;
  const byPath = new Map(items.map((it) => [it.to, it]));
  const out: NavItem[] = [];
  const seen = new Set<string>();
  for (const path of order) {
    const it = byPath.get(path);
    if (it && !seen.has(path)) {
      out.push(it);
      seen.add(path);
    }
  }
  for (const it of items) {
    if (!seen.has(it.to)) out.push(it);
  }
  return out;
}

/** 从 preferences 安全读 nav_order。 */
export function readNavOrder(prefs: Record<string, unknown> | undefined | null): string[] {
  const v = prefs?.nav_order;
  if (!Array.isArray(v)) return [];
  return v.filter((x): x is string => typeof x === "string");
}
